import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiBell, FiCheck, FiTrash2 } from 'react-icons/fi';
import { notificationsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { resolveMediaUrl } from '../../utils/media';
import './NotificationsDropdown.css';

const NotificationsDropdown = () => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const previousCountRef = useRef(0);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll({ limit: 10 });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unreadCount);
      previousCountRef.current = response.data.unreadCount;
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkForNewNotifications = useCallback(async () => {
    try {
      // Reuse /notifications which returns unreadCount to avoid an extra DB call.
      const response = await notificationsAPI.getAll({ limit: 1 });
      const newCount = Number(response.data.unreadCount || 0);
      const prevCount = previousCountRef.current;
      
      // Play sound if there are new notifications
      if (newCount > prevCount) {
        playNotificationSound();
      }
      
      setUnreadCount(newCount);
      previousCountRef.current = newCount;
      
      // Refresh full list if there are new ones
      if (newCount > prevCount) {
        loadNotifications();
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  }, [loadNotifications]);

  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();

      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        checkForNewNotifications();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, loadNotifications, checkForNewNotifications]);

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(err => console.log('Audio play failed:', err));
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Error al marcar como leídas');
    }
  };

  const handleDelete = async (notificationId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await notificationsAPI.delete(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notificación eliminada');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Error al eliminar');
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'like': return '❤️';
      case 'comment': return '💬';
      case 'reply': return '↩️';
      case 'follow': return '👤';
      case 'mention': return '@';
      default: return '🔔';
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = now - time;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `Hace ${days} día${days > 1 ? 's' : ''}`;
    if (hours > 0) return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
    if (minutes > 0) return `Hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    return 'Justo ahora';
  };

  if (!isAuthenticated) return null;

  return (
    <div className="notifications-container" ref={dropdownRef}>
      {/* Notification Sound */}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/mp3;base64,SUQzAwAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7v////////////////////////////////////////////////////////////////AAAAAExhdmM1OC4xMzAAAAAAAAAAAAAAAAskAAAAAAAAA4T+AAAAAAAAAAAAAAAAAAAAAP/7kGQAD/AAAGkAAAAIAAANIAAAAQAAAaQAAAAgAAA0gAAABExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV" type="audio/mp3"/>
      </audio>

      <button 
        className="notifications-button" 
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notificaciones"
      >
        <FiBell />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <h3>Notificaciones</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
              >
                <FiCheck /> Marcar todas como leídas
              </button>
            )}
          </div>

          <div className="notifications-list">
            {loading ? (
              <div className="notifications-loading">
                <div className="spinner"></div>
                <p>Cargando...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <FiBell size={48} />
                <p>No tienes notificaciones</p>
              </div>
            ) : (
              notifications.map(notification => (
                <Link
                  key={notification.id}
                  to={notification.link || '#'}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => {
                    if (!notification.is_read) {
                      handleMarkAsRead(notification.id);
                    }
                    setIsOpen(false);
                  }}
                >
                  <div className="notification-icon">
                    {notification.actor_avatar ? (
                      <img src={resolveMediaUrl(notification.actor_avatar)} alt="" />
                    ) : (
                      <span className="notification-emoji">
                        {getNotificationIcon(notification.type)}
                      </span>
                    )}
                  </div>
                  
                  <div className="notification-content">
                    <p className="notification-title">{notification.title}</p>
                    <p className="notification-message">{notification.message}</p>
                    <span className="notification-time">{formatTime(notification.created_at)}</span>
                  </div>

                  <button
                    className="notification-delete"
                    onClick={(e) => handleDelete(notification.id, e)}
                  >
                    <FiTrash2 />
                  </button>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
